import { Params } from './params'
import type { FailFn } from './schema/parse'
import type { SchemaSource } from './types'

// Every input container a method declares: an action's or trigger's `params`, a dictionary's `criteria`,
// a `dynamicParams.criteria`, the object a `dynamicParams.resolve()` returns, and a SYSTEM method's
// runtime-owned scheme. One class because they are the same thing — a z.object the runner validates input
// against and publishes as the method's input model.

export class MethodParams extends Params {
  static of(scheme?: unknown, source: SchemaSource = {}): MethodParams {
    return new MethodParams(scheme, source)
  }

  // An undeclared slot. A real instance, so callers never branch on undefined.
  static empty(): MethodParams {
    return new MethodParams(undefined)
  }

  // ── runtime ────────────────────────────────────────────────────────────────

  /**
   * Run the shared normalize → coerce → validate pipeline and hand the issues to `fail` (which throws).
   *
   * `carryUnknown` keeps the input's undeclared keys underneath the normalized ones. Two slots need it: an
   * action with `dynamicParams`, whose generated top-level fields are not in the static schema, and the
   * generated loaders, whose owner's `result` fn may read those same fields.
   */
  normalize<T extends Record<string, unknown>>(
    input: Record<string, unknown> | undefined,
    fail: FailFn,
    options: { carryUnknown?: boolean } = {}
  ): T {
    const { values, issues } = this.normalizeInput(input)

    if (issues.length) {
      fail(issues)
    }

    return (options.carryUnknown ? { ...input, ...values } : values) as T
  }
}
